import RelocationRv from "assets/images/psp/relatedContentImages/relocating-rv.webp";
import ctaMan from "assets/images/psp/inlineCTA.jpg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import HowToStore from "assets/images/psp/relatedContentImages/rvstoragebanner.webp";
import OutdoorRv from "assets/images/psp/relatedContentImages/outdoor-rv.webp";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import General from "assets/images/psp/relatedContentImages/winter-road-warrior-jpg.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/rv-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/rv-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/rv-header-tablet.webp";
import { storageCategories } from "../utils/getPSPLinks";

const RVFAQs = [
	{
		question: "Should I store my RV indoors?",
		answer: `
Indoor RV storage is the premier choice for protecting your recreational vehicle investment. Indoor, drive-up access storage facilities shield your RV from harsh weather conditions, including UV rays, hail, snow, and temperature extremes that can cause paint fading, roof damage, and interior deterioration.

Benefits of indoor RV storage include:
- Protection from weather-related damage and premature aging
- Reduced maintenance costs over time
- Enhanced security with controlled access
- Climate-controlled options that prevent moisture buildup
- Preservation of your RV's resale value

While [indoor storage costs](https://storage.com/blog/how-much-does-rv-storage-cost/) are higher than outdoor options, the long-term savings on repairs and maintenance justify the investment for many RV owners.`
	},
	{
		question: "How much is insurance for an RV?",
		answer: `
RV insurance costs vary significantly based on your vehicle type, usage, and coverage level. On average, RV owners pay between $1,000 and $4,000 annually for comprehensive coverage.

Cost factors include:
- RV type and value: Class A motorhomes ($2,000–$4,000+), Class B/C ($1,200–$2,500), travel trailers ($200–$1,000)
- Usage frequency: Full-time vs. seasonal use affects premiums
- Storage location: Secure storage facilities may qualify for discounts
- Coverage level: Liability-only vs. comprehensive with collision
- Driver experience: RV driving history and safety courses can reduce costs

Many insurance providers offer storage discounts of 10–25% when your RV is kept at a professional storage facility rather than at home. Some policies also include coverage for personal belongings and attached equipment, making comprehensive coverage valuable for expensive RVs.`
	},
	{
		question: "How do I weatherproof my RV?",
		answer: `
Proper weatherproofing extends your RV's lifespan and prevents costly repairs. Professional storage facilities often provide climate-controlled environments, but additional weatherproofing steps ensure maximum protection.

Essential weatherproofing steps:

Exterior protection:
- Inspect and reseal all seams, windows, and roof penetrations annually
- Apply UV-protective wax or coating to prevent paint and fiberglass degradation
- Check and replace weatherstripping around doors and windows
- Install or repair awning covers and slide-out seals

Interior preparation:
- Use moisture absorbers to prevent condensation and mold growth
- Clean and treat leather and fabric surfaces
- Remove perishables and drain all water systems
- Run dehumidifiers in climate-controlled storage

Seasonal considerations:
- Winterization: add antifreeze to plumbing systems and perform battery maintenance
- Summer prep: focus on cooling system checks and tire pressure monitoring

Professional storage facilities often provide weatherproofing services and climate-controlled environments that minimize your preparation requirements while ensuring optimal protection.`
	},
	{
		question: "Is RV storage a good investment?",
		answer: `
Professional RV storage is a strong investment for most RV owners, considering the total cost of ownership. The average RV depreciates 20% in the first year and 10–15% annually thereafter, but proper storage significantly slows this depreciation.

Financial benefits of professional storage:
- Preservation of value: Indoor storage can reduce depreciation by 30–50% compared to outdoor home storage
- Maintenance savings: Professional facilities prevent weather damage that averages $2,000–$5,000 annually
- Insurance benefits: Many providers offer 10–25% discounts for secure storage
- Convenience value: Professional facilities often include maintenance services and easy access
`
	},
	{
		question: "How often should you start an RV in storage?",
		answer: `
Regular operation prevents mechanical issues and maintains your RV's systems during extended storage periods. Most RV manufacturers and mechanics recommend starting stored RVs every 2–4 weeks, with monthly operation being the optimal frequency for most situations.

Recommended maintenance schedule:
- **Every 2 weeks:** Start the engine and run for 15–20 minutes to circulate fluids and charge batteries.
- **Monthly:** Drive the RV for 10–15 minutes to prevent tire flat spots and exercise systems.
- **Seasonally:** Complete system checks, including generator, appliances, and slide-outs.`
	}
];

const rvHerodescription = `
RV owners from all walks of life face a single, universal challenge: finding a safe and convenient place to park and store their beloved recreational vehicle. For years, those seeking nearby RV storage were forced to park their RVs in their front yards or on some family-owned pasture just outside of town. Luckily, RV storage facilities offer owners across the country a wide range of RV storage options, from indoor RV storage to outdoor RV parking spaces that offer safety, security, and accessibility.
`;

export const PSPRVContent = {
	type: "RV",
	sections: [
		{
			title: "RV Storage hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top RV Storage Units",
						description: rvHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find RV Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "RV Storage Hero Image"
					}
				}
			]
		},

		{
			title: "RV Storage Size Chart",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "Understanding Your RV Storage Needs",
						description:
							"Wondering what RV storage options are best for you? Let Storage.com break it down so you can choose the right option for your RV or trailer.",
						storages: [
							{
								title: "Indoor RV Storage",
								numerics: ["10x30", "10x40"],
								bestFor: [
									"Premium coverage",
									"Weather protection",
									"Higher security"
								]
							},
							{
								title: "Outdoor RV Storage",
								numerics: ["10x30", "10x40", "10x50"],
								bestFor: [
									"Budget-conscious owners",
									"Mild climates",
									"Short to medium-term storage"
								]
							},
							{
								title: "Covered RV Storage",
								numerics: ["10x30", "10x40", "10x50"],
								bestFor: [
									"Moderate protection",
									"Balance your budget with features",
									"Large size capacity"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "RV Storage Unit Size Guide",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "RV Storage Unit Size Guide",
						description:
							"### Camper Classification ###\n\nDon't get stuck with storage that doesn't fit. Here's your sizing cheat sheet.",
						content: [
							{
								id: "1",
								value: `- **Class A Motorhomes:** Need 40+ feet of length (these vehicles require specialized facilities for indoor storage)

- **Class B Motorhomes:** Require 25+ feet (the most storage-friendly option)

- **Class C Motorhomes:** Need 30+ feet of space

**Height alert:** Most RVs are around 13.5 feet tall. Standard storage units can't handle that—you need RV-specific facilities.`
							}
						],
						background: "light-blue",
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "What size storage unit do I need?",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "What size storage unit do I need to store my RV?",
						content: [
							{
								id: "1",
								value: `**10x15 or 10x20**
  
   - Ideal for smaller campers, trailers, or pop-up style recreational vehicles. 
   - This size option is available for indoor RV and camper storage. Comparable outdoor storage space dimensions may be closer to 8x20 or 10x20.`
							},
							{
								id: "2",
								value: `**10x40**
  
  - Class A motorhomes, like fifth wheel trailers and large RVs, require a storage space of 10x40 or longer for easy access and proper storage dimensions. 
  - These camper storage spots are easy to find as outdoor units, but some facilities may offer indoor storage that meets height, length, and width requirements.
  `
							},
							{
								id: "3",
								value: `**10x25 or 10x30**
  
  - Class B motorhomes, including camper vans and smaller RV models, can be comfortably stored in a 10x25 or [10x30 storage unit](https://storage.com/blog/10x30-storage-unit-size-guide/) or RV storage space. 
  - These units are commonly found at most RV storage facilities and offer 250–300 sq. feet of storage space.
  `
							},
							{
								id: "4",
								value: `**10x30 or 10x40+**
  
 - Class C RVs, small haulers, mid-size trailers, and medium-sized motorhomes benefit from 10x30, 10x40, or larger spaces, depending on model and dimensions.
 - Spaces this large (300–400 sq. ft.) are most commonly found as outdoor or covered RV storage units.
  `
							},
							{
								id: "5",
								value: `**Keep Height in Mind**

The sky is not the limit when it comes to RV storage. Measure the clearance height of your RV and ensure your motorhome storage spot can accommodate its height in addition to its width and length.`
							}
						],
						background: "blue",
						isColumnWise: false,
						listType: "Single"
					}
				}
			]
		},
		{
			title: "RV Rich Content",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Store Your RV or Camper in Every Season",
						description:
							"Most people store their motorhomes in the winter, but worry about [how to winterize an RV](https://storage.com/blog/winterize-your-rv/). Setting your camper up for storage success across all seasons is easier than you think. With just a few preparations and some forward thinking, you can safely store your RV so it’s ready for whatever adventure awaits. ",
						mediaUrl: "qNPob3z4DO0",
						mediaType: "video",
						videoTitle: "How to Winterize An RV"
					}
				}
			]
		},
		{
			title: "RV Storage Inline CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "RV",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						imageAltText: "inlineCTA image",
						background: "white"
					}
				}
			]
		},
		{
			title: "Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: {
						title: "Storage.com has 4.4 out of 5 star rating on",
						testimonials: [
							{
								body: "The storage I was able to find for my RV was good and I felt safe with the facility. The staff was friendly and professional.",
								author: "James Smith",
								title: "Great location and service",
								imageSrc: GeorgeRansom
							},
							{
								body: "I'm very pleased.",
								author: "Trane DePriest",
								title: "Reserving RV space was easy",
								imageSrc: AnnAlarcon
							},
							{
								body: "Needed a space for a visiting cousin to park a trailer for a four-day visit. I called Storage.com and they immediately found a space with great pricing close to our house. The reservation took less than 5 minutes and I got a call from the storage unit manager within 10 minutes. I was very grateful and relieved to be able to make these arrangements so easily.",
								author: "George Ransom",
								title: "Storage.com is easy, quick, and responsive",
								imageSrc: SMurphy
							}
						]
					}
				}
			]
		},
		{
			title: "Collapsible FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "RV Storage FAQs",
						faqs: RVFAQs
					}
				}
			]
		},
		{
			title: "Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About RV Storage",
						unitCards: [
							{
								category: "Moving Advice",
								content:
									"Relocating RVs: An Expert Guide to Finding RV Relocation Deals and Cheap Motorhomes for Rent",
								date: "August 25, 2025",
								image: RelocationRv,
								url: "https://www.storage.com/blog/relocating-rvs/"
							},
							{
								category: "Recreational Storage",
								content: "How Much Does RV Storage Cost?",
								date: "August 27, 2025",
								image: HowToStore,
								url: "https://www.storage.com/blog/how-much-does-rv-storage-cost/"
							},
							{
								category: "Recreational Storage",
								content:
									"How to Winterize an RV: Complete Guide with Tips",
								date: "August 27, 2025",
								image: General,
								url: "https://www.storage.com/blog/winterize-your-rv/"
							},
							{
								category: "Recreational Storage",
								content:
									"Where to Store Your RV: Pros and Cons of RV Storage Solutions",
								date: "August 27, 2025",
								image: OutdoorRv,
								url: "https://www.storage.com/blog/where-to-store-your-rv-trailer-or-camper/"
							}
						]
					}
				}
			]
		},
		{
			title: "RV  PSP Links",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.rv
						}
					}
				}
			]
		},
		{
			title: "RV Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						pageType: "PSP",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPRVContent;
